import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { PlusIcon } from '../components/icons.js';
import { PageTitle } from '../components/Layouts.js';
import {
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SeverityBadge,
  StatusBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatNumber } from '../i18n.js';

export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => unwrap(api.GET('/sites')),
    refetchInterval: 60_000,
  });
}

/** S2: all sites with status, 30-day uptime and open problems. */
export function Overview() {
  const { t, i18n } = useTranslation();
  const sites = useSites();
  const addLink = (
    <Link
      to="/sites/new"
      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-blue-700 px-4 font-semibold text-white hover:bg-blue-800"
    >
      <PlusIcon />
      {t('overview.add')}
    </Link>
  );

  let content;
  if (sites.isPending) content = <Loading />;
  else if (sites.isError)
    content = (
      <LoadError message={errorMessage(sites.error, t)} onRetry={() => void sites.refetch()} />
    );
  else if (sites.data.items.length === 0)
    content = (
      <Card>
        <h2 className="text-xl font-semibold">{t('overview.empty')}</h2>
        <p className="mt-2 text-slate-800">{t('overview.emptyText')}</p>
      </Card>
    );
  else {
    const down = sites.data.items.filter((s) => s.uptimeState === 'down').length;
    content = (
      <>
        {down > 0 ? (
          <div className="mb-4">
            <Notice kind="error">{t('banner.openIncidents', { count: down })}</Notice>
          </div>
        ) : null}
        <ul className="grid gap-4 sm:grid-cols-2">
          {sites.data.items.map((site) => {
            const problems = SEVERITIES.filter((s) => site.openFindings[s] > 0);
            return (
              <li key={site.id}>
                <Card className="h-full">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h2 className="text-lg font-semibold">
                      <LinkButton
                        to={`/sites/${site.id}`}

                        aria-label={t('overview.open', { name: site.name })}
                        variant="soft"
                        size="sm"
                      >
                        {site.name}
                      </LinkButton>
                    </h2>
                    <StatusBadge state={site.status === 'paused' ? 'paused' : site.uptimeState} />
                  </div>
                  <p className="mt-1 break-all text-sm text-slate-700">{site.url}</p>
                  <dl className="mt-4 grid grid-cols-2 gap-2">
                    <div>
                      <dt className="text-sm text-slate-700">{t('overview.uptime30d')}</dt>
                      <dd className="text-xl font-semibold">
                        {site.uptime30d === null
                          ? t('overview.noData')
                          : `${formatNumber(site.uptime30d, i18n.language)} %`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-700">{t('overview.problems')}</dt>
                      <dd className="mt-1 flex flex-wrap gap-1">
                        {problems.length === 0 ? (
                          <span>{t('overview.noProblems')}</span>
                        ) : (
                          problems.map((s) => (
                            <span key={s} className="inline-flex items-center gap-1">
                              <SeverityBadge severity={s} />
                              <span>{site.openFindings[s]}</span>
                            </span>
                          ))
                        )}
                      </dd>
                    </div>
                  </dl>
                </Card>
              </li>
            );
          })}
        </ul>
      </>
    );
  }
  return (
    <>
      <PageTitle actions={addLink}>{t('overview.title')}</PageTitle>
      {content}
    </>
  );
}
